import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/translations';

const Services = () => {
  const { language } = useLanguage();
  const t = translations[language];

  const services = [
    {
      id: 1,
      title: t.service1Title,
      description: t.service1Desc,
      image: '',
      features: language === 'en' ? [
        'HD & 4K Cameras',
        'Indoor & Outdoor Systems',
        'Night Vision Capability',
        'Remote Monitoring',
        'Motion Detection',
        'Professional Installation'
      ] : [
        'HD और 4K कैमरे',
        'इनडोर और आउटडोर सिस्टम',
        'रात्रि दृष्टि क्षमता',
        'दूरस्थ निगरानी',
        'गति पहचान',
        'पेशेवर स्थापना'
      ],
      icon: '📹'
    },
    {
      id: 2,
      title: t.service2Title,
      description: t.service2Desc,
      image: '',
      features: language === 'en' ? [
        'AI-Powered Detection',
        'Facial Recognition',
        'Smart Alerts',
        'Cloud Storage',
        'Mobile App Access',
        '24/7 Monitoring'
      ] : [
        'AI-संचालित पहचान',
        'चेहरे की पहचान',
        'स्मार्ट अलर्ट',
        'क्लाउड स्टोरेज',
        'मोबाइल ऐप एक्सेस',
        '24/7 निगरानी'
      ],
      icon: '🤖'
    },
    {
      id: 3,
      title: t.service3Title,
      description: t.service3Desc,
      image: '',
      features: language === 'en' ? [
        'Access Control Systems',
        'Alarm Systems',
        'Security Integration',
        '24/7 Monitoring',
        'Custom Solutions',
        'After-Sales Support'
      ] : [
        'एक्सेस कंट्रोल सिस्टम',
        'अलार्म सिस्टम',
        'सुरक्षा एकीकरण',
        '24/7 निगरानी',
        'कस्टम समाधान',
        'बिक्री के बाद सहायता'
      ],
      icon: '🔒'
    }
  ];

  return (
    <section id="services" className="py-24 bg-black relative overflow-hidden text-white">
      {/* CCTV Grid Background */}
      <div className="absolute inset-0 security-grid opacity-5"></div>
      
      {/* Scan Lines */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute w-full h-0.5 bg-gradient-to-r from-transparent via-green-500/30 to-transparent animate-scan-line"></div>
      </div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-20">
          <div className="inline-block px-6 py-2 bg-black/60 border-2 border-green-500/50 rounded mb-6">
            <span className="text-green-400 font-semibold font-mono text-sm tracking-wider">{t.servicesBadge}</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-extrabold mb-6 font-mono">
            {t.servicesTitle} <span className="bg-gradient-to-r from-green-400 to-cyan-400 bg-clip-text text-transparent">{t.servicesTitle2}</span>
          </h2>
          <p className="text-xl md:text-2xl text-gray-400 max-w-3xl mx-auto font-mono">
            {t.servicesSubtitle}
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-green-500 to-cyan-500 mx-auto mt-6 rounded-full"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {services.map((service, index) => (
            <div
              key={service.id}
              className="group relative bg-gray-900 rounded-lg shadow-2xl overflow-hidden hover:shadow-green-500/20 transition-all duration-500 transform hover:-translate-y-2 border-2 border-green-500/30 hover:border-green-500/60"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* CCTV Feed Effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-green-500/0 to-cyan-500/0 group-hover:from-green-500/10 group-hover:to-cyan-500/10 transition-all duration-500 z-0"></div>
              
              {/* Recording Indicator */}
              <div className="absolute top-4 left-4 z-20 flex items-center space-x-2 bg-black/80 px-3 py-1 rounded border border-red-500/50">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                <span className="text-xs text-red-400 font-mono">REC</span>
              </div>
              
              <div className="relative z-10">
                <div className="relative h-72 overflow-hidden bg-gray-800">
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                    <div className="text-center">
                      <div className="text-6xl mb-4">{service.icon}</div>
                      <div className="text-green-400 font-mono text-lg">{service.title}</div>
                    </div>
                  </div>
                  {/* CCTV Overlay Effect */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                  
                  {/* Grid Overlay */}
                  <div className="absolute inset-0 security-grid opacity-5"></div>
                  
                  {/* Icon Badge */}
                  <div className="absolute top-6 right-6 bg-black/80 backdrop-blur-sm border-2 border-green-500/50 rounded-lg p-4 shadow-lg transform group-hover:scale-110 transition-all duration-300">
                    <span className="text-4xl">{service.icon}</span>
                  </div>
                  
                  {/* Title Overlay */}
                  <div className="absolute bottom-6 left-6 right-6">
                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-2 drop-shadow-lg font-mono">
                      {service.title.toUpperCase()}
                    </h3>
                  </div>
                </div>
                
                <div className="p-8 bg-gray-900">
                  <p className="text-gray-400 mb-6 leading-relaxed text-base">
                    {service.description}
                  </p>
                  
                  <div className="space-y-3">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center group/item">
                        <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-cyan-500 rounded-lg flex items-center justify-center mr-3 flex-shrink-0 group-hover/item:scale-110 transition-transform shadow-lg shadow-green-500/50">
                          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <span className="text-gray-300 font-medium">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  {/* CTA Button */}
                  <button className="mt-8 w-full py-3 px-6 bg-gradient-to-r from-green-600 to-cyan-600 text-white rounded-lg font-semibold hover:from-green-700 hover:to-cyan-700 transition-all duration-300 transform group-hover:scale-105 shadow-lg shadow-green-500/50 border-2 border-green-400/50 font-mono">
                    {t.serviceButton}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Why Choose Us Section */}
        <div className="relative mt-20">
          <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-black rounded-lg p-8 md:p-16 border-2 border-green-500/30 shadow-2xl overflow-hidden">
            {/* Security Grid Background */}
            <div className="absolute inset-0 security-grid opacity-5"></div>
            
            <div className="relative z-10 max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <div className="flex items-center justify-center mb-4">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse-glow mr-3"></div>
                  <h3 className="text-4xl md:text-5xl font-extrabold font-mono">
                    {t.servicesWhyTitle}
                  </h3>
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse-glow ml-3"></div>
                </div>
                <p className="text-xl text-gray-400 max-w-2xl mx-auto font-mono">
                  {t.servicesWhySubtitle}
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  {
                    icon: '⚡',
                    title: t.servicesWhy1,
                    description: t.servicesWhy1Desc,
                    gradient: 'from-green-500 to-cyan-500'
                  },
                  {
                    icon: '🛠️',
                    title: t.servicesWhy2,
                    description: t.servicesWhy2Desc,
                    gradient: 'from-cyan-500 to-green-500'
                  },
                  {
                    icon: '✅',
                    title: t.servicesWhy3,
                    description: t.servicesWhy3Desc,
                    gradient: 'from-green-400 to-emerald-500'
                  }
                ].map((item, index) => (
                  <div key={index} className="bg-gray-800/80 backdrop-blur-sm rounded-lg p-8 border-2 border-green-500/30 hover:border-green-500/60 transform hover:scale-105 transition-all duration-300">
                    <div className={`w-20 h-20 bg-gradient-to-br ${item.gradient} rounded-lg flex items-center justify-center mb-6 mx-auto text-4xl shadow-lg shadow-green-500/50`}>
                      {item.icon}
                    </div>
                    <h4 className="text-2xl font-bold mb-3 text-center font-mono text-white">{item.title}</h4>
                    <p className="text-gray-400 text-center">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;

